/**
 * Render-based geometry lint rules. Each is a pure function of the frame snapshots on the runtime
 * context (measured element boxes/opacity/appearance per probe point) — no timeline driving, no DOM
 * mutation. Thresholds are the codeo layout-measurer's, ported verbatim.
 */
import { describeElement } from './measure';
import type { ElementMeasurement, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';
import { findNearAlignedPairs, type AlignmentEntry } from './alignment';

const OPACITY_PRESENT_THRESHOLD = 0.05;
const TEXT_UNREADABLE_PCT = 2.5;
const TEXT_TOO_SMALL_PCT = 4.5;
const SAFE_MARGIN = 80;
const FULL_BLEED_THRESHOLD = 0.92;
const RECT_EPS = 0.5;
const OVERLAP_TOL_PX = 4;
const MIN_ALIGN_SIZE_PX = 24;

// An element (or a group, via an ancestor) the author has marked as deliberately reaching past the
// canvas edge: a fly-on/fly-off, a mid-scene exit, a scatter that bleeds off the frame. The bounds
// rules skip it entirely. The failure these rules exist to catch is content left outside the frame by
// accident, so an explicit "yes, I meant this" is the whole signal they need — anything narrower
// (a window, a phase) also rules out intents that are perfectly reasonable.
const ALLOW_OFFSCREEN_ATTR = '[data-l5-allow-offscreen]';

// The escape hatch, phrased for each of the two rules that honour it.
const OFFSCREEN_HATCH_OVERFLOW =
    'If reaching past the edge is the intent — a fly-on/fly-off, or a deliberate bleed — add ' +
    'data-l5-allow-offscreen to that element (or to a wrapper around the group) and this rule will ' +
    'leave it alone.';
const OFFSCREEN_HATCH_MARGIN =
    'If it is meant to reach past the edge, add data-l5-allow-offscreen to that element (or to a ' +
    'wrapper around the group).';

// The overlap of two rects on each axis; negative when they don't intersect on that axis.
function overlapExtent(a: Rect, b: Rect): { width: number; height: number } {
    const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
    return { width, height };
}

function isFullBleed(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    return (
        rect.width >= canvasWidth * FULL_BLEED_THRESHOLD &&
        rect.height >= canvasHeight * FULL_BLEED_THRESHOLD
    );
}

function overflowsCanvas(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    return (
        rect.x < -RECT_EPS ||
        rect.y < -RECT_EPS ||
        rect.x + rect.width > canvasWidth + RECT_EPS ||
        rect.y + rect.height > canvasHeight + RECT_EPS
    );
}

// True when an edge floats inside the safe-margin gutter: strictly inside the canvas (not flush to
// the edge, which reads as intentional bleed) but closer than SAFE_MARGIN to it.
function intrudesGutter(rect: Rect, canvasWidth: number, canvasHeight: number): boolean {
    const gaps = [
        rect.x,
        rect.y,
        canvasWidth - (rect.x + rect.width),
        canvasHeight - (rect.y + rect.height),
    ];
    return gaps.some((gap) => gap > RECT_EPS && gap < SAFE_MARGIN);
}

interface ElementPresence {
    element: Element;
    /** This element's measurements across the frames where it is actually visible. */
    frames: ElementMeasurement[];
}

// Group measurements by element identity (the same Element object recurs across frames, since every
// probe measures the one loaded document), keeping only frames where the element is visible.
function presentElements(context: RuntimeLintContext): ElementPresence[] {
    const byElement = new Map<Element, ElementMeasurement[]>();
    for (const frame of context.frames ?? []) {
        for (const measurement of frame.elements) {
            if (measurement.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            const list = byElement.get(measurement.element) ?? [];
            list.push(measurement);
            byElement.set(measurement.element, list);
        }
    }
    return [...byElement].map(([element, frames]) => ({ element, frames }));
}

/**
 * The smallest rendered text this element ever shows, in px and as a % of canvas height, or null when
 * it renders no measurable text. Both units are reported: the percentage is the rule's threshold, but
 * px is what the author actually types.
 */
function minRenderedText(
    presence: ElementPresence,
    canvasHeight: number,
): { px: number; pct: number } | null {
    const sizes = presence.frames
        .filter((m) => m.hasText && m.fontSizePx > 0)
        .map((m) => m.fontSizePx);
    if (!sizes.length) {
        return null;
    }
    const px = Math.min(...sizes);
    return { px, pct: (px / canvasHeight) * 100 };
}

/**
 * The smallest font size in whole px that satisfies `pctThreshold` on this canvas. Rounded up, so an
 * author who sets exactly this number clears the threshold rather than landing a fraction under it.
 */
function thresholdPx(pctThreshold: number, canvasHeight: number): number {
    return Math.ceil((pctThreshold / 100) * canvasHeight);
}

/**
 * Text rendered below TEXT_UNREADABLE_PCT of the canvas height is illegible at normal viewing
 * distances. This is a hard floor — even ambient text (watermarks, credits) must clear it.
 */
export const textBelowMinSize: RuntimeLintRule = {
    id: 'text-below-min-size',
    severity: 'error',
    guidance: `Render every on-screen text element at no less than ${TEXT_UNREADABLE_PCT}% of the canvas height — below that it is unreadable at normal viewing distances. This floor applies even to ambient text.`,
    check(context: RuntimeLintContext): string[] {
        const canvasHeight = context.canvasSize?.height;
        if (!canvasHeight) return [];
        const floorPx = thresholdPx(TEXT_UNREADABLE_PCT, canvasHeight);
        const messages: string[] = [];
        for (const presence of presentElements(context)) {
            const size = minRenderedText(presence, canvasHeight);
            if (size !== null && size.pct < TEXT_UNREADABLE_PCT) {
                messages.push(
                    `${describeElement(presence.element)} renders text at ${Math.floor(size.px)}px ` +
                        `(${size.pct.toFixed(2)}% of this ${canvasHeight}px-tall canvas) — below the ` +
                        `${TEXT_UNREADABLE_PCT}% readable floor. Raise its font size to at least ` +
                        `${floorPx}px.`,
                );
            }
        }
        return messages;
    },
};

/**
 * Text in the [TEXT_UNREADABLE_PCT, TEXT_TOO_SMALL_PCT) band is legible but smaller than the norm
 * for motion-graphic body text — an advisory warning. Deliberately subtle text (watermark,
 * attribution, disclaimer, footer credit) can opt out with `data-l5-text-role="ambient"`. Text
 * already below the unreadable floor is left to text-below-min-size, so the two never double-report.
 */
export const textBelowRecommendedSize: RuntimeLintRule = {
    id: 'text-below-recommended-size',
    severity: 'warning',
    guidance: `Aim for on-screen text at ${TEXT_TOO_SMALL_PCT}% of the canvas height or larger. If a piece is intentionally subtle (watermark, attribution, disclaimer, footer credit), mark it \`data-l5-text-role="ambient"\` to silence this.`,
    check(context: RuntimeLintContext): string[] {
        const canvasHeight = context.canvasSize?.height;
        if (!canvasHeight) return [];
        const normPx = thresholdPx(TEXT_TOO_SMALL_PCT, canvasHeight);
        const messages: string[] = [];
        for (const presence of presentElements(context)) {
            const size = minRenderedText(presence, canvasHeight);
            if (size === null || size.pct < TEXT_UNREADABLE_PCT || size.pct >= TEXT_TOO_SMALL_PCT) {
                continue;
            }
            if (presence.element.closest('[data-l5-text-role="ambient"]')) continue;
            messages.push(
                `${describeElement(presence.element)} renders text at ${Math.floor(size.px)}px ` +
                    `(${size.pct.toFixed(2)}% of this ${canvasHeight}px-tall canvas), below the ` +
                    `${TEXT_TOO_SMALL_PCT}% norm for motion-graphic text — that would be ${normPx}px ` +
                    `here. Increase the font size, or mark it data-l5-text-role="ambient" if it is ` +
                    `intentionally subtle.`,
            );
        }
        return messages;
    },
};

// Visit each present element measurement once, in frame order, reporting each element at most once
// (the first frame that trips `hit`). Used by the per-frame geometry rules.
function firstOffendingFrame(
    context: RuntimeLintContext,
    hit: (rect: Rect, canvasWidth: number, canvasHeight: number) => boolean,
): Array<{ element: Element; progress: number }> {
    const size = context.canvasSize;
    if (!size) return [];
    const reported = new Set<Element>();
    const results: Array<{ element: Element; progress: number }> = [];
    for (const frame of context.frames ?? []) {
        for (const measurement of frame.elements) {
            if (measurement.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            if (reported.has(measurement.element)) continue;
            if (!hit(measurement.rect, size.width, size.height)) continue;
            // Checked only once the frame already offends, so the ancestor walk costs nothing on the
            // elements — nearly all of them — that are sitting where they should.
            if (measurement.element.closest(ALLOW_OFFSCREEN_ATTR)) continue;
            reported.add(measurement.element);
            results.push({ element: measurement.element, progress: frame.progress });
        }
    }
    return results;
}

/**
 * An element whose rendered box extends past the canvas edge (and is not a deliberate full-bleed
 * background) has content clipped off-frame. Reported at the first frame it overflows, unless it is
 * marked `data-l5-allow-offscreen`.
 */
export const contentOverflowsCanvas: RuntimeLintRule = {
    id: 'content-overflows-canvas',
    severity: 'error',
    guidance:
        'Keep on-screen content within the canvas bounds. A full-bleed background is fine, but any ' +
        'other element extending past the edge is clipped off-frame. Mark an element ' +
        '`data-l5-allow-offscreen` when reaching past the edge is the design — it flies in or out of ' +
        'frame, or it is a scatter meant to bleed off — and this rule will not judge its bounds.',
    check(context: RuntimeLintContext): string[] {
        const offenders = firstOffendingFrame(
            context,
            (rect, w, h) => overflowsCanvas(rect, w, h) && !isFullBleed(rect, w, h),
        );
        return offenders.map(
            ({ element, progress }) =>
                `${describeElement(element)} extends past the canvas edge at progress ` +
                `${progress} — content is clipped off-frame. Move or resize it to sit within the ` +
                `canvas. ${OFFSCREEN_HATCH_OVERFLOW}`,
        );
    },
};

/**
 * An element sitting inside the canvas but within the SAFE_MARGIN gutter reads as cramped at the
 * edge. Advisory: intentional edge bleed (flush to the edge) and full-bleed backgrounds are exempt,
 * as is anything marked `data-l5-allow-offscreen` — an element allowed past the edge crosses the
 * gutter by definition, and would otherwise just trade the overflow error for this warning.
 */
export const contentInSafeMargin: RuntimeLintRule = {
    id: 'content-in-safe-margin',
    severity: 'warning',
    guidance:
        `Keep on-screen content out of the ${SAFE_MARGIN}px safe-margin gutter at the canvas edges ` +
        '(unless it is a deliberate full-bleed background, a flush edge bleed, or marked ' +
        '`data-l5-allow-offscreen`).',
    check(context: RuntimeLintContext): string[] {
        const offenders = firstOffendingFrame(
            context,
            (rect, w, h) =>
                !isFullBleed(rect, w, h) &&
                !overflowsCanvas(rect, w, h) && // overflow rule owns those
                intrudesGutter(rect, w, h),
        );
        return offenders.map(
            ({ element, progress }) =>
                `${describeElement(element)} sits inside the ${SAFE_MARGIN}px safe-margin gutter ` +
                `at progress ${progress} — inside the canvas but cramped at the edge. Move it ` +
                `further in. ${OFFSCREEN_HATCH_MARGIN}`,
        );
    },
};

/**
 * Two text glyph regions that share a screen region in the same frame — the "new text fades in
 * before the old text in the same spot fades out" failure. Text-on-text only (a shape or icon under
 * text is intentional layering). A shared or per-element `data-l5-allow-overlap` opts a deliberate
 * crossfade out. Advisory.
 */
export const textOverlap: RuntimeLintRule = {
    id: 'text-overlap',
    severity: 'warning',
    guidance:
        'Do not let two pieces of text occupy the same spot at the same time — sequence them so one ' +
        'fades out before the next reveals, or reposition them. Add data-l5-allow-overlap if the ' +
        'crossfade is intentional.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        // Stable per-element index for order-independent pair keys.
        const indexOf = new Map<Element, number>();
        const keyFor = (a: Element, b: Element): string => {
            for (const el of [a, b]) if (!indexOf.has(el)) indexOf.set(el, indexOf.size);
            const [x, y] = [indexOf.get(a)!, indexOf.get(b)!].sort((m, n) => m - n);
            return `${x}|${y}`;
        };
        for (const frame of context.frames ?? []) {
            const texts = frame.elements.filter(
                (m) => m.hasText && m.opacity >= OPACITY_PRESENT_THRESHOLD,
            );
            for (let i = 0; i < texts.length; i++) {
                for (let j = i + 1; j < texts.length; j++) {
                    const a = texts[i];
                    const b = texts[j];
                    if (
                        a.element.closest('[data-l5-allow-overlap]') ||
                        b.element.closest('[data-l5-allow-overlap]')
                    ) {
                        continue;
                    }
                    // Nested text (a parent holding mixed inline content and its inline child) always
                    // "overlaps" — the child's ink sits inside the parent's. That's one piece of copy,
                    // not two colliding, so skip ancestor/descendant pairs.
                    if (a.element.contains(b.element) || b.element.contains(a.element)) continue;
                    const overlap = overlapExtent(a.inkRect, b.inkRect);
                    if (overlap.width <= OVERLAP_TOL_PX || overlap.height <= OVERLAP_TOL_PX)
                        continue;
                    const key = keyFor(a.element, b.element);
                    if (reportedPairs.has(key)) continue;
                    reportedPairs.add(key);
                    messages.push(
                        `${describeElement(a.element)} and ${describeElement(b.element)} overlap ` +
                            `at progress ${frame.progress} — text appears in the same spot before ` +
                            `the other has cleared. Sequence or reposition them, or add ` +
                            `data-l5-allow-overlap if the crossfade is intentional.`,
                    );
                }
            }
        }
        return messages;
    },
};

/**
 * Two decorative shapes that are contained one-in-the-other and *almost* concentric — the
 * hand-computed-coordinates near-miss. Text is excluded (its box centering is a different concern),
 * as are shapes under 24px. Advisory; reported once per pair across frames.
 */
export const elementsNearAligned: RuntimeLintRule = {
    id: 'elements-near-aligned',
    severity: 'warning',
    guidance:
        'When one shape sits inside another, center it exactly or offset it clearly — an ' +
        'almost-but-not-quite-centered shape reads as a mistake.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        for (const frame of context.frames ?? []) {
            const shapes = frame.elements.filter(
                (m) =>
                    !m.hasText &&
                    m.opacity >= OPACITY_PRESENT_THRESHOLD &&
                    m.rect.width >= MIN_ALIGN_SIZE_PX &&
                    m.rect.height >= MIN_ALIGN_SIZE_PX,
            );
            const entries: AlignmentEntry[] = shapes.map((m, index) => ({
                label: String(index),
                rect: m.rect,
            }));
            for (const pair of findNearAlignedPairs(entries)) {
                const inner = shapes[Number(pair.element)].element;
                const outer = shapes[Number(pair.element2)].element;
                const key = [describeElement(inner), describeElement(outer)].sort().join('|');
                if (reportedPairs.has(key)) continue;
                reportedPairs.add(key);
                messages.push(
                    `${describeElement(inner)} is almost—but not exactly—centered inside ` +
                        `${describeElement(outer)} (off by ${pair.dx}px, ${pair.dy}px). Center it ` +
                        `exactly or offset it clearly.`,
                );
            }
        }
        return messages;
    },
};
